import { h, Fragment } from 'preact';
import { useState, useEffect, useRef } from 'preact/hooks';
import styles from './ProductCard.module.scss';
import { ProductBadge, extractBadgesFromProduct } from './ProductBadge';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';
function parseColorSwatches(product) {
    if (!product.metafields || product.metafields.length === 0) {
        return [];
    }
    // Find the grouping metafield with namespace "bac" and key "grouping"
    const groupingMetafield = product.metafields.find((metafield) => metafield.namespace === 'bac' && metafield.key === 'grouping');
    if (!groupingMetafield || !groupingMetafield.value) {
        return [];
    }
    try {
        const parsedData = JSON.parse(groupingMetafield.value);
        return Array.isArray(parsedData) ? parsedData : [];
    }
    catch (error) {
        return [];
    }
}
function parsePromotionCallout(product) {
    var _a, _b, _c, _d;
    if (!product.metafields || product.metafields.length === 0) {
        return null;
    }
    // Only show promotion callouts when there's a compare-at price
    if (!product.price.sale) {
        return null;
    }
    // First, find the promotion_labels metafield with value "Sale"
    const promotionSaleMetafield = product.metafields.find((metafield) => metafield.namespace === 'lab' && metafield.key === 'promotion_labels' && metafield.value === 'Sale');
    if (!promotionSaleMetafield) {
        return null;
    }
    // Then, find the promotion_callouts metafield with the same ID
    const promotionCalloutMetafield = product.metafields.find((metafield) => metafield.namespace === 'bac' &&
        metafield.key === 'promotion_callouts' &&
        metafield.id === promotionSaleMetafield.id);
    if (!promotionCalloutMetafield || !promotionCalloutMetafield.value) {
        // Fallback: try to find any promotion_callouts if no ID match
        const fallbackCalloutMetafield = product.metafields.find((metafield) => metafield.namespace === 'bac' && metafield.key === 'promotion_callouts');
        if (!fallbackCalloutMetafield || !fallbackCalloutMetafield.value) {
            return null;
        }
        try {
            const parsed = JSON.parse(fallbackCalloutMetafield.value);
            // Handle new JSON structure with array of name/value objects
            if (Array.isArray(parsed) && parsed.length >= 2) {
                const firstValue = ((_a = parsed[0]) === null || _a === void 0 ? void 0 : _a.value) || '';
                const secondValue = ((_b = parsed[1]) === null || _b === void 0 ? void 0 : _b.value) || '';
                return firstValue && secondValue ? `${firstValue}: ${secondValue}` : null;
            }
            // Fallback for old structure
            return parsed.text || null;
        }
        catch (error) {
            console.error('Error parsing fallback promotion callout:', error);
            return null;
        }
    }
    try {
        const parsed = JSON.parse(promotionCalloutMetafield.value);
        // Handle new JSON structure with array of name/value objects
        if (Array.isArray(parsed) && parsed.length >= 2) {
            const firstValue = ((_c = parsed[0]) === null || _c === void 0 ? void 0 : _c.value) || '';
            const secondValue = ((_d = parsed[1]) === null || _d === void 0 ? void 0 : _d.value) || '';
            return firstValue && secondValue ? `${firstValue}: ${secondValue}` : null;
        }
        // Fallback for old structure
        return parsed.text || null;
    }
    catch (error) {
        console.error('Error parsing promotion callout:', error);
        return null;
    }
}
// Helper function to check if promotion label is "Sale"
function hasPromotionLabelSale(product) {
    if (!product.metafields || product.metafields.length === 0) {
        return false;
    }
    const promotionLabelsMetafield = product.metafields.find((metafield) => metafield.namespace === 'lab' && metafield.key === 'promotion_labels' && metafield.value === 'Sale');
    return !!promotionLabelsMetafield;
}
// Helper function to get stock availability tag
function getStockAvailabilityTag(product) {
    var _a, _b;
    if (!product.metafields || product.metafields.length === 0) {
        return null;
    }
    // console.log(product.metafields);
    // const stockAvailabilityMetafield = product.metafields.find(
    //   (metafield) => metafield.namespace === 'lab' && metafield.key === 'stock_availability_tag'
    // );
    const stockAvailabilityMetafield = product.metafields.filter(metafield => metafield.namespace === 'lab' && metafield.key === 'stock_availability_tag');
    let originalTag = null;
    if (stockAvailabilityMetafield.length === 1) {
        originalTag = (_a = stockAvailabilityMetafield[0]) === null || _a === void 0 ? void 0 : _a.value;
    }
    else if (stockAvailabilityMetafield.length > 1) {
        const stockAvailabilityOrder = ["in stock", "in stock soon", "available for order", "out of stock"];
        // Loop through keywords in order to find the first matching metafield value
        let matchedTag = null;
        for (const keyword of stockAvailabilityOrder) {
            const matchingMetafield = stockAvailabilityMetafield.find(item => item.value.toLowerCase() === keyword);
            if (matchingMetafield) {
                matchedTag = matchingMetafield.value;
                break;
            }
        }
        if (matchedTag) {
            originalTag = matchedTag;
        }
        else {
            originalTag = (_b = stockAvailabilityMetafield[0]) === null || _b === void 0 ? void 0 : _b.value;
        }
    }
    // Handle case where the value might be a JSON string that gets parsed as an array
    if (originalTag && typeof originalTag === 'string') {
        try {
            const parsed = JSON.parse(originalTag);
            if (Array.isArray(parsed) && parsed.length > 0) {
                originalTag = parsed[0];
            }
        }
        catch (error) {
            // If it's not valid JSON, keep the original string
        }
    }
    // // Check if the first variant has inventory_quantity of 0 or less
    // if (product.variants && product.variants.length > 0) {
    //   const firstVariant = product.variants[0];
    //   if (firstVariant.inventory_quantity !== undefined && firstVariant.inventory_quantity <= 0) {
    //     return 'Out of Stock';
    //   }
    // }
    return originalTag;
}
// Helper function to render stock availability UI
function renderStockAvailability(tag) {
    // Ensure tag is a string
    if (Array.isArray(tag)) {
        tag = tag[0] || '';
    }
    if (typeof tag !== 'string') {
        tag = String(tag || '');
    }
    if (tag === 'In Stock') {
        return (h(Fragment, null,
            h("div", { className: styles.stockIcon + ' ' + styles.inStockIcon },
                h("svg", { className: styles.tickIcon, xmlns: "http://www.w3.org/2000/svg", width: "10", height: "10", viewBox: "0 0 14 14", fill: "none" },
                    h("path", { d: "M12.1352 2L5.97813 8.08525L2.96898 5.14552L2 6.17116L5.97815 10L13 2.95705L12.1352 2Z", fill: "white", stroke: "white", strokeWidth: "1" }))),
            h("span", { className: styles.inStock }, tag)));
    }
    else if (tag === 'In Stock soon') {
        return (h(Fragment, null,
            h("div", { className: styles.stockIcon + ' ' + styles.inStockSoonIcon }),
            h("span", { className: styles.inStockSoon }, tag)));
    }
    else if (tag === 'Available for order') {
        return (h(Fragment, null,
            h("div", { className: styles.stockIcon + ' ' + styles.availableForOrderIcon }),
            h("span", { className: styles.availableForOrder }, tag)));
    }
    else if (tag === 'Out of Stock') {
        return (h(Fragment, null,
            h("div", { className: styles.stockIcon + ' ' + styles.outOfStockIcon }),
            h("span", { className: styles.outOfStock }, tag)));
    }
    else {
        return h("span", null, tag);
    }
}
// Helper to generate Shopify CDN image URLs with width and format
function getShopifyImageUrl(baseUrl, width, format) {
    if (!baseUrl)
        return '';
    try {
        const url = new URL(baseUrl);
        url.searchParams.set('width', String(width));
        if (format)
            url.searchParams.set('format', format);
        return url.toString();
    }
    catch (_a) {
        // fallback for relative URLs or if URL constructor fails
        let url = baseUrl.includes('?') ? baseUrl + '&' : baseUrl + '?';
        url += `width=${width}`;
        if (format)
            url += `&format=${format}`;
        return url;
    }
}
// Helper function to format price with proper decimal places
function formatPrice(price) {
    return price % 1 === 0 ? price.toString() : price.toFixed(2);
}
// Helper function to get set_of metafield value
function getSetOfValue(metafields) {
    if (!metafields)
        return null;
    const setOfField = metafields.find(field => field.namespace === 'bac' && field.key === 'set_of');
    if (setOfField && setOfField.value) {
        const value = parseInt(setOfField.value);
        return isNaN(value) ? null : value;
    }
    return null;
}
function hasSizeVariants(product) {
    // If there is only one variant, it means it only has one size
    if (!product.variants || product.variants.length === 1) {
        return false;
    }
    return product.variants.some(variant => variant.merged_options &&
        variant.merged_options.some(option => typeof option === 'string' && option.toLowerCase().includes('size')));
}
export const ProductCard = ({ product, layout = 'grid', eager = false, columns = 2 }) => {
    var _a, _b, _c;
    const { title, price, image, images, productUrl, vendor } = product;
    const [isMobile, setIsMobile] = useState(false);
    const [isInWishlist, setIsInWishlist] = useState(false);
    const [imageLoaded, setImageLoaded] = useState(false);
    const [imageError, setImageError] = useState(false);
    const imgRef = useRef(null);
    const { topBadges, bottomBadges } = extractBadgesFromProduct(product);
    const colorSwatches = parseColorSwatches(product);
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 768);
        };
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);
    // Check if product is already in wishlist
    useEffect(() => {
        const checkWishlistStatus = () => {
            if (window._swat && window._swat.fetchWrtEventTypeET) {
                window._swat.fetchWrtEventTypeET((products) => {
                    // Check if current product is in wishlist
                    const isInWishlist = products.find((p) => {
                        return p.empi === productData.empi && p.epi === productData.epi;
                    });
                    setIsInWishlist(!!isInWishlist);
                }, 4); // Event type 4 = wishlist
            }
        };
        // Delay the check to ensure Swym is loaded
        const timeoutId = setTimeout(checkWishlistStatus, 500);
        return () => clearTimeout(timeoutId);
    }, []);
    const isImageObj = (img) => typeof img === 'object' && 'url' in img;
    const imgUrl = isImageObj(image) ? image.url : image;
    const imgAlt = isImageObj(image) ? image.alt : title;
    const renderOptimizedImage = (src, alt, isSlideImage = false) => {
        const [slideImageLoaded, setSlideImageLoaded] = useState(false);
        return (h("picture", null,
            h("source", { srcSet: `
            ${getShopifyImageUrl(src, 180, 'avif')} 180w,
            ${getShopifyImageUrl(src, 360, 'avif')} 360w,
            ${getShopifyImageUrl(src, 540, 'avif')} 540w
          `, type: "image/avif" }),
            h("source", { srcSet: `
            ${getShopifyImageUrl(src, 180, 'webp')} 180w,
            ${getShopifyImageUrl(src, 360, 'webp')} 360w,
            ${getShopifyImageUrl(src, 540, 'webp')} 540w
          `, type: "image/webp" }),
            h("source", { srcSet: `
            ${getShopifyImageUrl(src, 180)} 180w,
            ${getShopifyImageUrl(src, 360)} 360w,
            ${getShopifyImageUrl(src, 540)} 540w
          `, type: "image/jpeg" }),
            h("img", { ref: !isSlideImage ? imgRef : undefined, src: getShopifyImageUrl(src, 180), alt: alt, sizes: "(max-width: 480px) 50vw, (max-width: 768px) 33vw, (max-width: 1200px) 25vw, 180px", loading: eager ? 'eager' : 'lazy', fetchpriority: eager ? 'high' : 'auto', className: isSlideImage ? (slideImageLoaded ? styles.imageLoaded : '') : (imageLoaded ? styles.imageLoaded : ''), onLoad: () => {
                    if (isSlideImage) {
                        setSlideImageLoaded(true);
                    }
                    else {
                        setImageLoaded(true);
                    }
                }, onError: () => !isSlideImage && setImageError(true) })));
    };
    const renderImage = () => {
        if (isMobile && images && images.length > 0) {
            return (h(Swiper, { spaceBetween: 0, slidesPerView: 1, className: styles.productSwiper, modules: [Pagination], pagination: {
                    clickable: true,
                    el: `.${styles.swiperPagination}`,
                } },
                images.map((img, index) => {
                    const imgSrc = isImageObj(img) ? img.url : img;
                    return (h(SwiperSlide, { key: index }, renderOptimizedImage(imgSrc, `${imgAlt} - ${index + 1}`, true)));
                }),
                h("div", { className: styles.swiperPagination })));
        }
        if (images && images.length > 1) {
            const hoverImageUrl = isImageObj(images[1]) ? images[1].url : images[1];
            return (h("div", { className: styles.hoverImageContainer },
                h("div", { className: styles.mainImage }, renderOptimizedImage(imgUrl, imgAlt)),
                h("div", { className: styles.hoverImage }, renderOptimizedImage(hoverImageUrl, `${imgAlt}-hover`))));
        }
        else {
            return renderOptimizedImage(imgUrl, imgAlt);
        }
    };
    // Create dynamic product data for Swym wishlist
    const productData = {
        epi: parseInt(((_b = (_a = product.variants) === null || _a === void 0 ? void 0 : _a[0]) === null || _b === void 0 ? void 0 : _b.id) || product.first_variant_id || product.id),
        empi: parseInt(product.id),
        du: `https://www.urbansales.co.nz/products/${product.handle || productUrl}`,
        pr: Math.round(parseFloat(price.regular.toString()) * 100),
        iu: imgUrl,
        source: "plp",
        et: 4,
        cart: "",
        uri: window.location.href,
        dt: document.title,
        ct: "",
        rc: "default",
        type: "product-variant",
        ru: window.location.href,
    };
    const addToWishlist = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!window._swat)
            return;
        if (!isInWishlist) {
            window._swat.addToWishList(productData, () => {
                setIsInWishlist(true);
            });
        }
        else {
            window._swat.removeFromWishList(productData, () => {
                setIsInWishlist(false);
            });
        }
    };
    const currentPath = window.location.pathname;
    let formattedProductUrl = `/products/${productUrl}`;
    // For Breadcrumb to show which collection it was from
    if (currentPath.includes('/collections')) {
        formattedProductUrl = `${currentPath}/products/${productUrl}`;
    }
    return (h("a", { href: formattedProductUrl, className: styles.productLink },
        h("div", { className: layout === 'grid' ? styles.productCard : styles.productCardList, "data-columns": columns },
            h("div", { className: "klaviyo-favorites-plp-slot", "data-product-id": product.id, "data-product-url": `/products/${product.handle}`, "data-variant-id": product.first_variant_id }),
            h("div", { className: styles.productImage },
                renderImage(),
                h("button", { className: `${styles.wishlistButton} ${isInWishlist ? styles.wishlistActive : ''}`, "aria-label": isInWishlist ? 'Remove from wishlist' : 'Add to wishlist', type: "button", onClick: addToWishlist },
                    h("svg", { width: "20", height: "18", viewBox: "0 0 20 18", fill: "none", xmlns: "http://www.w3.org/2000/svg" },
                        h("path", { d: "M10 16.5L8.55 15.1995C4.4 11.36 1.5 8.69 1.5 5.5C1.5 3.14 3.39 1.25 5.75 1.25C7.07 1.25 8.34 1.86 10 2.93C11.66 1.86 12.93 1.25 14.25 1.25C16.61 1.25 18.5 3.14 18.5 5.5C18.5 8.69 15.6 11.36 11.45 15.1995L10 16.5Z", fill: isInWishlist ? '#e53935' : 'none', stroke: isInWishlist ? '#e53935' : '#333', strokeWidth: "1.5" }))),
                h("div", { className: styles.topBadgeContainer }, topBadges.map(({ key, badge, position, color }) => (h(ProductBadge, { key: key, badge: badge, position: position, color: color })))),
                h("div", { className: styles.bottomBadgeContainer }, bottomBadges.map(({ key, badge, position, color }) => (h(ProductBadge, { key: key, badge: badge, position: position, color: color }))))),
            h("div", { className: styles.productInfo },
                h("div", { className: styles.productBrandAndRating },
                    vendor && (h("div", { className: styles.productVendor }, vendor)),
                    ((_c = product.review_count) !== null && _c !== void 0 ? _c : 0) > 0 ?
                        (h("div", { className: styles.productRatingContainer },
                            h("svg", { width: "15", height: "14", viewBox: "0 0 15 14", fill: "none", xmlns: "http://www.w3.org/2000/svg" },
                                h("path", { d: "M7.5 0L5.18241 4.60852L0 5.34759L3.75 8.93481L2.86483 14L7.5 11.6085L12.1352 14L11.25 8.93481L15 5.34759L9.81759 4.60852L7.5 0Z", fill: "#D29126" })),
                            h("span", { className: styles.productRating }, product.review_ratings))) : undefined),
                h("h3", { className: styles.productTitle }, title),
                h("div", { className: styles.productPrice },
                    h("div", { className: styles.priceLeft },
                        h("span", { className: `${styles.regularPrice} ${price.sale ? styles.regularPriceOnSale : ''}` },
                            "$",
                            formatPrice(price.regular)),
                        price.sale && (h("span", { className: styles.salePrice },
                            "$",
                            formatPrice(price.sale)))),
                    (() => {
                        const setOfValue = getSetOfValue(product.metafields);
                        if (setOfValue && setOfValue > 1) {
                            const perUnitPrice = Math.floor((price.regular / setOfValue) * 100) / 100;
                            const perUnitPriceClass = price.sale ? styles.perUnitPriceOnSale : styles.perUnitPriceRegular;
                            return (h("span", { className: `${styles.perUnitPrice} ${perUnitPriceClass}` },
                                "$",
                                formatPrice(perUnitPrice),
                                "/ea."));
                        }
                        return null;
                    })()),
                hasSizeVariants(product) && (h("div", { className: styles.sizeVariantsText }, "Available in other sizes")),
                colorSwatches.length > 0 && (h("div", { className: styles.colorSwatches }, colorSwatches.map((swatch, index) => {
                    return (h("div", { key: `${swatch.product_id}-${index}`, className: styles.colorSwatch, style: {
                            backgroundImage: swatch.colour_image ? `url(${swatch.colour_image})` : undefined,
                            backgroundColor: swatch.colour_code || '#dfdfdf',
                            backgroundSize: swatch.colour_image ? 'cover' : undefined,
                            backgroundPosition: swatch.colour_image ? 'center' : undefined,
                            backgroundRepeat: swatch.colour_image ? 'no-repeat' : undefined,
                        }, title: swatch.name }));
                }))),
                getStockAvailabilityTag(product) && (h("div", { className: styles.productAvailability }, renderStockAvailability(getStockAvailabilityTag(product)))),
                hasPromotionLabelSale(product) && parsePromotionCallout(product) && (h("div", { className: styles.promotionCallouts }, parsePromotionCallout(product)))))));
};
